import type { ReactNode, MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';

type Variant = 'primary' | 'outline' | 'light' | 'ghost';
type Size = 'md' | 'lg';

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  icon?: string;
  download?: string;
};

type ButtonAsLink = CommonProps & { to: string; href?: never; onClick?: never; type?: never };
type ButtonAsAnchor = CommonProps & { href: string; to?: never; onClick?: never; type?: never };
type ButtonAsButton = CommonProps & {
  to?: never;
  href?: never;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
  disabled?: boolean;
};

type ButtonProps = ButtonAsLink | ButtonAsAnchor | ButtonAsButton;

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-label text-[13px] uppercase tracking-[0.18em] transition-colors duration-300 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60';

const sizes: Record<Size, string> = {
  md: 'px-6 py-3',
  lg: 'px-8 py-4',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-primary-500 text-background-50 hover:bg-primary-400 border border-primary-500 hover:border-primary-400',
  outline:
    'bg-transparent text-foreground-950 border border-primary-500/60 hover:border-primary-400 hover:text-primary-300',
  light:
    'bg-background-950 text-background-50 hover:bg-background-900 border border-background-950',
  ghost:
    'bg-transparent text-primary-300 border border-transparent hover:text-primary-200',
};

export default function Button(props: ButtonProps) {
  const { children, variant = 'primary', size = 'md', className = '', icon, download } = props;
  const classes = `${base} ${sizes[size]} ${variants[variant]} ${className}`;
  const inner = (
    <>
      {icon ? <i className={`${icon} text-base`} aria-hidden="true"></i> : null}
      <span>{children}</span>
    </>
  );

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={classes}>
        {inner}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    return (
      <a href={props.href} download={download} className={classes}>
        {inner}
      </a>
    );
  }

  const buttonProps = props as ButtonAsButton;
  return (
    <button
      type={buttonProps.type || 'button'}
      onClick={buttonProps.onClick}
      disabled={buttonProps.disabled}
      className={classes}
    >
      {inner}
    </button>
  );
}